import axios from "axios";
import { API_BASE_URL } from "./apiBase";

const API_BASE = API_BASE_URL;
const STORAGE_KEY = "access_token";

let accessToken = null;
let refreshPromise = null;
/** @type {null | ((token: string | null) => void)} */
let onTokenUpdated = null;
/** @type {null | (() => void)} */
let onSessionEnded = null;

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || null;
  if (token) {
    localStorage.setItem(STORAGE_KEY, token);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  if (onTokenUpdated) {
    onTokenUpdated(accessToken);
  }
}

export function clearSession() {
  accessToken = null;
  localStorage.removeItem(STORAGE_KEY);
  if (onTokenUpdated) {
    onTokenUpdated(null);
  }
}

/**
 * Register React AuthContext sync hooks (called once from AuthProvider).
 */
export function registerAuthSessionListeners({ onTokenUpdated: onUpdate, onSessionEnded: onEnded }) {
  onTokenUpdated = onUpdate;
  onSessionEnded = onEnded;
}

export function isAuthExemptUrl(url = "") {
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/login") ||
    url.includes("/auth/signup") ||
    url.includes("/auth/logout")
  );
}

export function isTokenExpiredError(error) {
  if (!error?.response || error.response.status !== 401) {
    return false;
  }
  const message = error.response.data?.message;
  if (Array.isArray(message)) {
    return message.some((m) => String(m).includes("Token has expired"));
  }
  return message === "Token has expired";
}

/**
 * Refresh access token using HttpOnly refresh cookie. Single-flight.
 * @returns {Promise<string>} new access token
 */
export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const response = await axios.post(
      `${API_BASE}/auth/refresh`,
      null,
      { withCredentials: true }
    );
    const newAccessToken =
      response.data.access_token || response.data.accessToken;
    if (!newAccessToken) {
      throw new Error("No access token in refresh response");
    }
    setAccessToken(newAccessToken);
    return newAccessToken;
  })();

  try {
    return await refreshPromise;
  } catch (error) {
    clearSession();
    if (onSessionEnded) {
      onSessionEnded();
    }
    throw error;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Clear server refresh cookie (best-effort).
 */
export async function logoutOnServer() {
  try {
    await axios.post(`${API_BASE}/auth/logout`, null, {
      withCredentials: true,
    });
  } catch (error) {
    // Cookie may already be gone; ignore
    console.warn("Logout cookie clear failed:", error?.message || error);
  }
}

export { API_BASE, STORAGE_KEY };
